import { formatCurrency, formatILS } from '../domain/money.ts';
import type { Transaction } from '../domain/types.ts';

export { formatILS, formatCurrency };

const DATE_FORMAT = new Intl.DateTimeFormat('he-IL', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

/** 'YYYY-MM-DD' -> 'dd.mm.yy', without constructing a timezone-sensitive Date. */
export function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return DATE_FORMAT.format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
}

const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric' });
const MONTH_NAME_FORMAT = new Intl.DateTimeFormat('he-IL', { month: 'long' });

export function formatMonth(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  if (!year || !month) return monthKey;
  return MONTH_YEAR_FORMAT.format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));
}

/** Just the month name — for lists already grouped under a year heading. */
export function formatMonthName(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  if (!year || !month) return monthKey;
  return MONTH_NAME_FORMAT.format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));
}

const BUILD_FORMAT = new Intl.DateTimeFormat('he-IL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * The build stamp, as 'dd.mm.yyyy HH:MM' in the reader's own timezone.
 *
 * A real `Date` here, unlike everywhere else in this file: the build time is a
 * genuine instant, not a calendar day, and it is the one value in the app that
 * has to be compared across machines — the whole point is answering "is the
 * tab I have open older than the one I just deployed".
 */
export function formatBuildTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return BUILD_FORMAT.format(parsed);
}

/** Human summary of a foreign-currency row, for the amount tooltip. */
export function originalAmountLabel(transaction: Transaction): string | null {
  if (transaction.originalAmount === null || transaction.originalCurrency === null) return null;
  return `${formatCurrency(transaction.originalAmount, transaction.originalCurrency)} → ${formatILS(
    transaction.amountILS,
  )}`;
}

export function installmentLabel(transaction: Transaction): string | null {
  if (transaction.installment === null) return null;
  const { current, total, totalAmount } = transaction.installment;
  return `תשלום ${current}/${total} · ${formatILS(totalAmount)} סה"כ`;
}
